import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, Loader2, Sun, CloudSun, Building, Users, Timer, AlertCircle, ClipboardList, ArrowUpDown, ArrowUp, ArrowDown, Trash2, UserPlus, X, Edit } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import InfoBanner from '../../components/ui/info-banner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchAttendance = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  
  // Company/Subcontractor selection
  const [selectedCompany, setSelectedCompany] = useState('fortis');
  const [subcontractors, setSubcontractors] = useState([]);
  
  // Inner tab selection
  const [activeTab, setActiveTab] = useState('daily'); // 'daily', 'monthly', 'overtime'
  
  // Daily attendance state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [dailyAttendance, setDailyAttendance] = useState([]);
  const [subcontractorAttendance, setSubcontractorAttendance] = useState({}); // {employee_id: {sabah_time, oglen_time}}
  const [loading, setLoading] = useState(true);
  
  // Monthly attendance state
  const [monthlyDate, setMonthlyDate] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  
  // Overtime state
  const [overtimeYear, setOvertimeYear] = useState(new Date().getFullYear());
  const [overtimeMonth, setOvertimeMonth] = useState(new Date().getMonth() + 1);
  const [overtimeData, setOvertimeData] = useState([]);
  const [overtimeLoading, setOvertimeLoading] = useState(false);
  
  // Leaves (İzinler) state
  const [leavesYear, setLeavesYear] = useState(new Date().getFullYear());
  const [leavesData, setLeavesData] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [leavesSortColumn, setLeavesSortColumn] = useState(null);
  const [leavesSortDirection, setLeavesSortDirection] = useState('asc');
  
  // Sorting state for daily attendance
  const [dailySortConfig, setDailySortConfig] = useState({ key: null, direction: 'asc' });

  // Günlük mesai notları (şube yöneticisi hatırlatma notu)
  const [attendanceNotes, setAttendanceNotes] = useState({}); // {employee_id: {note, updated_by_name, updated_at}}
  const [monthlyNotes, setMonthlyNotes] = useState({}); // {"employee_id|YYYY-MM-DD": {note,...}}
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteEmployee, setNoteEmployee] = useState(null);
  const [noteDate, setNoteDate] = useState(null); // hangi güne not (null => currentDate)
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // TAŞERON BAZLI Mesai Takip Görevi State
  const [subcontractorAssignments, setSubcontractorAssignments] = useState({}); // {subcontractor_id: {employee_id, employee_name}}
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedSubcontractorForTask, setSelectedSubcontractorForTask] = useState(null);
  const [selectedSubcontractorsForTask, setSelectedSubcontractorsForTask] = useState([]); // çoklu taşeron seçimi
  const [selectedEmployeeForTask, setSelectedEmployeeForTask] = useState('');
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [allBranchEmployees, setAllBranchEmployees] = useState([]); // FORTİS kadrolu çalışanlar
  
  // Mesai takip bildirimleri
  const [mesaiNotifications, setMesaiNotifications] = useState([]);

  // Mesai bildirimleri yükle ve otomatik okundu işaretle
  const loadMesaiNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'mesai_takip', is_read: false },
        headers: getAuthHeaders()
      });
      const notifications = res.data || [];
      
      // Bildirimler varsa göster ve 3 saniye sonra otomatik okundu yap
      if (notifications.length > 0) {
        setMesaiNotifications(notifications);
        
        // 3 saniye sonra otomatik okundu işaretle (kullanıcı görsün diye)
        setTimeout(async () => {
          try {
            for (const notif of notifications) {
              await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
            }
            // Bildirimleri temizle
            setMesaiNotifications([]);
            // Unread count'u güncelle
            window.dispatchEvent(new Event('unread-counts-updated'));
          } catch (error) {
            console.error('Bildirimler otomatik okundu yapılamadı:', error);
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Mesai bildirimleri yüklenemedi:', error);
    }
  }, []);
  
  // Tüm mesai bildirimlerini hemen okundu olarak işaretle (manuel buton için)
  const markAllMesaiNotificationsAsRead = async () => {
    try {
      for (const notif of mesaiNotifications) {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      }
      setMesaiNotifications([]);
      window.dispatchEvent(new Event('unread-counts-updated'));
    } catch (error) {
      console.error('Bildirimler okundu yapılırken hata:', error);
      toast.error('Bildirimler güncellenemedi');
    }
  };

  // Taşeron bazlı atamaları yükle
  const loadSubcontractorAssignments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branch/subcontractor-attendance-assignments`, {
        headers: getAuthHeaders()
      });
      // Convert array to object map: {subcontractor_id: assignment}
      const assignmentsMap = {};
      (res.data || []).forEach(a => {
        assignmentsMap[a.subcontractor_id] = {
          employee_id: a.assigned_employee_id,
          employee_name: a.employee_name
        };
      });
      setSubcontractorAssignments(assignmentsMap);
    } catch (error) {
      console.error('Taşeron atamaları yüklenemedi:', error);
    }
  }, []);
  
  // Sayfa yüklendiğinde
  useEffect(() => {
    loadMesaiNotifications();
    loadSubcontractorAssignments();
  }, [loadMesaiNotifications, loadSubcontractorAssignments]);

  // Tarih formatla - yerel saat dilimine göre (Türkiye)
  const formatDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const formatDisplayDate = (date) => {
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
  };

  // Çalışanın işe giriş tarihi (start_date veya hire_date) - sadece tarih (saat yok)
  const getEmployeeStartDate = (emp) => {
    const raw = emp?.start_date || emp?.hire_date;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Verilen tarih, çalışanın işe giriş tarihinden ÖNCE mi? (işe girmeden önce listelenmesin)
  const isBeforeStartDate = (emp, dateObj) => {
    const sd = getEmployeeStartDate(emp);
    if (!sd) return false;
    const target = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    return target < sd;
  };

  // Fetch subcontractors on mount
  useEffect(() => {
    fetchSubcontractors();
  }, []);

  // Fetch employees and attendance when company or date changes
  useEffect(() => {
    fetchEmployees();
    fetchAttendanceNotes();
    if (selectedCompany === 'fortis') {
      fetchDailyAttendance();
    } else {
      fetchSubcontractorAttendance();
    }
  }, [selectedCompany, currentDate]);

  // Fetch monthly data when month changes
  useEffect(() => {
    if (activeTab === 'monthly') {
      fetchMonthlyAttendance();
      fetchMonthlyNotes();
    }
  }, [selectedCompany, monthlyDate, activeTab]);

  // Fetch overtime data when year/month changes
  useEffect(() => {
    if (activeTab === 'overtime') {
      fetchOvertimeData();
    }
  }, [selectedCompany, overtimeYear, overtimeMonth, activeTab]);

  // Fetch leaves data when year changes or company changes
  useEffect(() => {
    if (activeTab === 'leaves') {
      fetchLeavesData();
    }
  }, [leavesYear, activeTab, selectedCompany]);

  const fetchSubcontractors = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${API}/branch/subcontractors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubcontractors(response.data || []);
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      let response;
      try {
        response = await axios.get(`${API}/branch/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (branchErr) {
        response = await axios.get(`${API}/hr/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Filter by company/subcontractor
      let filtered = response.data || [];
      
      if (selectedCompany === 'fortis') {
        filtered = filtered.filter(emp => !emp.subcontractor_id);
      } else {
        filtered = filtered.filter(emp => emp.subcontractor_id === selectedCompany);
      }
      
      setEmployees(filtered);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Çalışanlar yüklenirken hata oluştu');
    }
    setLoading(false);
  };

  const fetchDailyAttendance = async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const dateStr = formatDateStr(currentDate);
      
      const response = await axios.get(`${API}/manager/daily-attendance?date=${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDailyAttendance(response.data?.records || []);
      setSubcontractorAttendance({});
    } catch (error) {
      console.error('Error fetching daily attendance:', error);
    }
  };

  // Günlük mesai notlarını çek
  const fetchAttendanceNotes = useCallback(async () => {
    try {
      const dateStr = formatDateStr(currentDate);
      const res = await axios.get(`${API}/manager/attendance-notes?date=${dateStr}`, { headers: getAuthHeaders() });
      setAttendanceNotes(res.data || {});
    } catch (error) {
      console.error('Mesai notları yüklenemedi:', error);
      setAttendanceNotes({});
    }
  }, [currentDate]);

  // Aya ait notları çek (Aylık Mesai için)
  const fetchMonthlyNotes = useCallback(async () => {
    try {
      const y = monthlyDate.getFullYear();
      const m = monthlyDate.getMonth() + 1;
      const res = await axios.get(`${API}/manager/attendance-notes/month?year=${y}&month=${m}`, { headers: getAuthHeaders() });
      setMonthlyNotes(res.data || {});
    } catch (error) {
      console.error('Aylık notlar yüklenemedi:', error);
      setMonthlyNotes({});
    }
  }, [monthlyDate]);

  // Not modalını aç (dateStr verilmezse günlük görünüm tarihi)
  const openNoteDialog = (emp, dateStr = null) => {
    const d = dateStr || formatDateStr(currentDate);
    setNoteEmployee(emp);
    setNoteDate(d);
    const existing = dateStr ? monthlyNotes[`${emp.id}|${d}`]?.note : attendanceNotes[emp.id]?.note;
    setNoteText(existing || '');
    setNoteDialogOpen(true);
  };

  // Notu kaydet
  const saveNote = async () => {
    if (!noteEmployee) return;
    setNoteSaving(true);
    try {
      const d = noteDate || formatDateStr(currentDate);
      await axios.put(`${API}/manager/attendance-note`, {
        employee_id: noteEmployee.id,
        employee_name: noteEmployee.name,
        date: d,
        note: noteText
      }, { headers: getAuthHeaders() });
      toast.success(noteText.trim() ? 'Not kaydedildi' : 'Not silindi');
      setNoteDialogOpen(false);
      setNoteEmployee(null);
      setNoteText('');
      setNoteDate(null);
      fetchAttendanceNotes();
      fetchMonthlyNotes();
    } catch (error) {
      console.error('Not kaydedilemedi:', error);
      toast.error(error.response?.data?.detail || 'Not kaydedilemedi');
    }
    setNoteSaving(false);
  };

  const fetchSubcontractorAttendance = async () => {
    if (selectedCompany === 'fortis') return;
    
    try {
      const token = localStorage.getItem('fortis_token');
      const dateStr = formatDateStr(currentDate);
      
      const response = await axios.get(`${API}/branch/subcontractor-attendance/${selectedCompany}?date=${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubcontractorAttendance(response.data?.attendance_map || {});
      setDailyAttendance([]);
    } catch (error) {
      console.error('Error fetching subcontractor attendance:', error);
      setSubcontractorAttendance({});
    }
  };

  const fetchMonthlyAttendance = async () => {
    setMonthlyLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const year = monthlyDate.getFullYear();
      const month = monthlyDate.getMonth() + 1;
      
      let response;
      try {
        response = await axios.get(`${API}/branch/attendance/monthly/${year}/${month}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (branchErr) {
        response = await axios.get(`${API}/hr/attendance/monthly/${year}/${month}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      let filtered = response.data || [];
      
      if (selectedCompany === 'fortis') {
        filtered = filtered.filter(emp => !emp.subcontractor_id);
      } else {
        filtered = filtered.filter(emp => emp.subcontractor_id === selectedCompany);
      }
      
      setMonthlyData(filtered);
    } catch (error) {
      console.error('Error fetching monthly attendance:', error);
      toast.error('Aylık mesai verileri yüklenirken hata oluştu');
    }
    setMonthlyLoading(false);
  };

  const fetchOvertimeData = async () => {
    setOvertimeLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      const overtimePromises = employees.map(async (emp) => {
        try {
          const response = await axios.get(`${API}/hr/employees/${emp.id}/overtime?year=${overtimeYear}&month=${overtimeMonth}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return { ...emp, overtime: response.data || [] };
        } catch {
          return { ...emp, overtime: [] };
        }
      });
      
      const results = await Promise.all(overtimePromises);
      setOvertimeData(results);
    } catch (error) {
      console.error('Error fetching overtime data:', error);
    }
    setOvertimeLoading(false);
  };

  // İzinler verisini çek
  const fetchLeavesData = async () => {
    setLeavesLoading(true);
    try {
      const response = await axios.get(`${API}/hr/attendance/yearly/${leavesYear}`, {
        headers: getAuthHeaders()
      });
      
      let data = response.data || [];
      
      // Seçilen şirkete göre filtrele
      if (selectedCompany === 'fortis') {
        // FORTİS kadrolu çalışanlar - subcontractor_id olmayan
        data = data.filter(emp => !emp.subcontractor_id);
      } else if (selectedCompany !== 'all') {
        // Belirli bir taşeron seçilmiş - sadece o taşeronun çalışanlarını göster
        data = data.filter(emp => emp.subcontractor_id === selectedCompany);
      }
      // 'all' seçiliyse tüm verileri göster (filtre yok)
      
      setLeavesData(data);
    } catch (error) {
      console.error('Error fetching leaves data:', error);
      toast.error('İzin verileri yüklenemedi');
      setLeavesData([]);
    }
    setLeavesLoading(false);
  };

  // İzinler sıralama handler
  const handleLeavesSort = (column) => {
    if (leavesSortColumn === column) {
      setLeavesSortDirection(leavesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLeavesSortColumn(column);
      setLeavesSortDirection('asc');
    }
  };

  // İzinler verilerini sırala
  const sortLeavesData = (a, b) => {
    if (leavesSortColumn === null) return 0;
    
    switch (leavesSortColumn) {
      case 'name':
        return leavesSortDirection === 'asc' 
          ? (a.name || '').localeCompare(b.name || '', 'tr')
          : (b.name || '').localeCompare(a.name || '', 'tr');
      case 'absent':
        const valA1 = a.totals?.absent || 0;
        const valB1 = b.totals?.absent || 0;
        return leavesSortDirection === 'asc' ? valA1 - valB1 : valB1 - valA1;
      case 'mazeret_izni':
        const valA2 = a.totals?.paid_leave || a.totals?.leave || 0;
        const valB2 = b.totals?.paid_leave || b.totals?.leave || 0;
        return leavesSortDirection === 'asc' ? valA2 - valB2 : valB2 - valA2;
      case 'ucretsiz_izin':
        const valA3 = a.totals?.unpaid_leave || 0;
        const valB3 = b.totals?.unpaid_leave || 0;
        return leavesSortDirection === 'asc' ? valA3 - valB3 : valB3 - valA3;
      case 'raporlu':
        const valA4 = a.totals?.sick || 0;
        const valB4 = b.totals?.sick || 0;
        return leavesSortDirection === 'asc' ? valA4 - valB4 : valB4 - valA4;
      case 'yillik_izin':
        const valA5 = a.totals?.annual || 0;
        const valB5 = b.totals?.annual || 0;
        return leavesSortDirection === 'asc' ? valA5 - valB5 : valB5 - valA5;
      default:
        return 0;
    }
  };

  const sortedLeavesData = [...leavesData].sort(sortLeavesData);
  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

  const getDailyAttendanceForEmployee = (employeeId, employeeName) => {
    // Check subcontractor attendance first (for non-fortis companies)
    if (selectedCompany !== 'fortis' && subcontractorAttendance[employeeId]) {
      const subAtt = subcontractorAttendance[employeeId];
      return {
        morning_time: subAtt.sabah_time,
        sabah_time: subAtt.sabah_time,
        afternoon_time: subAtt.oglen_time,
        oglen_time: subAtt.oglen_time
      };
    }
    
    // Check daily attendance for Fortis employees
    return dailyAttendance.find(a => 
      a.employee_id === employeeId || 
      (a.employee_name && a.employee_name.toLowerCase() === employeeName?.toLowerCase())
    );
  };

  const navigateDate = (days) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const navigateMonth = (direction) => {
    const newDate = new Date(monthlyDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setMonthlyDate(newDate);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getStatusBadge = (status) => {
    const config = {
      'tam_gun': { label: 'G', color: 'bg-green-500 text-white', title: 'Tam Gün' },
      'yarim_gun': { label: 'Y', color: 'bg-yellow-500 text-white', title: 'Yarım Gün' },
      'izinli': { label: 'İ', color: 'bg-blue-500 text-white', title: 'Ücretli İzinli' },
      'ucretsiz_izinli': { label: 'Üİ', color: 'bg-orange-500 text-white', title: 'Ücretsiz İzinli' },
      'raporlu': { label: 'R', color: 'bg-purple-500 text-white', title: 'Raporlu' },
      'yillik_izin': { label: 'Yİ', color: 'bg-cyan-500 text-white', title: 'Yıllık İzin' },
      'yok': { label: 'X', color: 'bg-red-500 text-white', title: 'Gelmedi' },
      'tatil': { label: 'T', color: 'bg-gray-400 text-white', title: 'Resmi Tatil' },
      'present': { label: 'G', color: 'bg-green-500 text-white', title: 'Tam Gün' },
      'half_day': { label: 'Y', color: 'bg-yellow-500 text-white', title: 'Yarım Gün' },
      'paid_leave': { label: 'İ', color: 'bg-blue-500 text-white', title: 'Ücretli İzinli' },
      'unpaid_leave': { label: 'Üİ', color: 'bg-orange-500 text-white', title: 'Ücretsiz İzinli' },
      'sick': { label: 'R', color: 'bg-purple-500 text-white', title: 'Raporlu' },
      'annual': { label: 'Yİ', color: 'bg-cyan-500 text-white', title: 'Yıllık İzin' },
      'absent': { label: 'X', color: 'bg-red-500 text-white', title: 'Gelmedi' },
      'holiday': { label: 'T', color: 'bg-gray-400 text-white', title: 'Resmi Tatil' }
    };
    const c = config[status] || { label: '-', color: 'bg-gray-200 text-gray-600', title: 'Kayıt Yok' };
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${c.color}`} title={c.title}>
        {c.label}
      </span>
    );
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '-';
    }
  };

  const handleDailySort = (key) => {
    let direction = 'asc';
    if (dailySortConfig.key === key && dailySortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setDailySortConfig({ key, direction });
  };

  const getSortedEmployees = () => {
    // İşe giriş tarihinden önceki günlerde çalışanı listeden çıkar
    const visibleEmployees = employees.filter(emp => !isBeforeStartDate(emp, currentDate));
    if (!dailySortConfig.key) return visibleEmployees;
    
    return [...visibleEmployees].sort((a, b) => {
      const attA = getDailyAttendanceForEmployee(a.id, a.name);
      const attB = getDailyAttendanceForEmployee(b.id, b.name);
      
      let valueA, valueB;
      
      if (dailySortConfig.key === 'morning') {
        const timeA = attA?.morning_time || attA?.sabah_time || '';
        const timeB = attB?.morning_time || attB?.sabah_time || '';
        valueA = timeA ? (typeof timeA === 'string' && timeA.includes(':') ? timeA : formatTime(timeA)) : 'zzz';
        valueB = timeB ? (typeof timeB === 'string' && timeB.includes(':') ? timeB : formatTime(timeB)) : 'zzz';
      } else if (dailySortConfig.key === 'afternoon') {
        const timeA = attA?.afternoon_time || attA?.oglen_time || '';
        const timeB = attB?.afternoon_time || attB?.oglen_time || '';
        valueA = timeA ? (typeof timeA === 'string' && timeA.includes(':') ? timeA : formatTime(timeA)) : 'zzz';
        valueB = timeB ? (typeof timeB === 'string' && timeB.includes(':') ? timeB : formatTime(timeB)) : 'zzz';
      } else if (dailySortConfig.key === 'name') {
        valueA = a.name || '';
        valueB = b.name || '';
      } else if (dailySortConfig.key === 'position') {
        valueA = a.position_name || a.gorev || '';
        valueB = b.position_name || b.gorev || '';
      } else if (dailySortConfig.key === 'profession') {
        valueA = a.profession || a.meslek || '';
        valueB = b.profession || b.meslek || '';
      }
      
      if (valueA < valueB) return dailySortConfig.direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return dailySortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (dailySortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return dailySortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-amber-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-amber-600" />;
  };

  const companyTabs = [
    { id: 'fortis', name: 'FORTİS', icon: Building },
    ...subcontractors.map(s => ({ id: s.id, name: s.name || s.company_name || 'Taşeron', icon: Users }))
  ];

  // TAŞERON BAZLI GÖREV ATAMA FONKSİYONLARI

  // Modal aç - belirli taşeron için
  const openTaskModalForSubcontractor = async (subcontractorId) => {
    setSelectedSubcontractorForTask(subcontractorId);
    setSelectedSubcontractorsForTask([subcontractorId]); // varsayılan: tıklanan taşeron seçili
    setTaskModalOpen(true);
    setLoadingAssignments(true);
    
    try {
      // FORTİS kadrolu çalışanları yükle
      let employeesRes;
      try {
        employeesRes = await axios.get(`${API}/branch/employees`, { headers: getAuthHeaders() });
        // Eğer boş dönerse HR endpoint'e fallback yap
        if (!employeesRes.data || employeesRes.data.length === 0) {
          employeesRes = await axios.get(`${API}/hr/employees`, { headers: getAuthHeaders() });
        }
      } catch (branchErr) {
        employeesRes = await axios.get(`${API}/hr/employees`, { headers: getAuthHeaders() });
      }
      
      const fortisEmployees = (employeesRes.data || []).filter(emp => !emp.subcontractor_id);
      setAllBranchEmployees(fortisEmployees);
      
      // Mevcut atama varsa seç
      const existingAssignment = subcontractorAssignments[subcontractorId];
      if (existingAssignment) {
        setSelectedEmployeeForTask(existingAssignment.employee_id);
      } else {
        setSelectedEmployeeForTask('');
      }
    } catch (error) {
      console.error('Çalışanlar yüklenemedi:', error);
      toast.error('Çalışanlar yüklenemedi');
    }
    
    setLoadingAssignments(false);
  };

  // Görev ata
  const handleAssignTask = async () => {
    if (!selectedEmployeeForTask) {
      toast.error('Lütfen bir çalışan seçin');
      return;
    }
    const targetSubs = selectedSubcontractorsForTask.length > 0
      ? selectedSubcontractorsForTask
      : (selectedSubcontractorForTask ? [selectedSubcontractorForTask] : []);
    if (targetSubs.length === 0) {
      toast.error('Lütfen en az bir taşeron seçin');
      return;
    }
    
    setTaskSubmitting(true);
    try {
      const res = await axios.post(`${API}/branch/subcontractor-attendance-assignment`, {
        subcontractor_ids: targetSubs,
        assigned_employee_id: selectedEmployeeForTask
      }, { headers: getAuthHeaders() });
      
      toast.success(res.data?.message || 'Mesai takip görevi atandı');
      setTaskModalOpen(false);
      loadSubcontractorAssignments();
    } catch (error) {
      console.error('Görev atanamadı:', error);
      toast.error(error.response?.data?.detail || 'Görev atanamadı');
    }
    setTaskSubmitting(false);
  };

  const toggleSubcontractorForTask = (subId) => {
    setSelectedSubcontractorsForTask((prev) =>
      prev.includes(subId) ? prev.filter((s) => s !== subId) : [...prev, subId]
    );
  };

  // Görevi kaldır
  const handleRemoveAssignment = async (subcontractorId) => {
    if (!window.confirm('Bu taşeron için mesai takip görevini kaldırmak istediğinize emin misiniz?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/branch/subcontractor-attendance-assignment/${subcontractorId}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('Görev kaldırıldı');
      loadSubcontractorAssignments();
    } catch (error) {
      console.error('Görev kaldırılamadı:', error);
      toast.error('Görev kaldırılamadı');
    }
  };

  // Taşeron adını bul - name veya company_name kullan
  const getSubcontractorName = (subId) => {
    const sub = subcontractors.find(s => s.id === subId);
    return sub?.name || sub?.company_name || 'Taşeron';
  };

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-attendance">
      {/* Bilgilendirme */}
      <InfoBanner id="branch-attendance-info" variant="info">
        <strong>Mesai Takip:</strong> Mesai Takip bölümünde Fortis ve diğer Taşeronların şubenizde görevlendirdiği kişilerin <strong>GÜNLÜK MESAİ</strong>, <strong>AYLIK MESAİ</strong>, <strong>FAZLA MESAİLERİNİ</strong> görüntüleyebilirsiniz. 
        İstediğiniz kişiye mesai görevi atayabilir ve bu kişi anlık olarak belirlediğiniz taşeronun mesai takibini girer ve gün içinde şubenizde aktif kaç kişinin çalıştığını öğrenebilirsiniz.
      </InfoBanner>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mesai Takibi</h1>
          <p className="text-gray-500">{user?.branch_name} şubesi çalışanlarının mesai durumları</p>
        </div>
      </div>
      
      {/* Mesai Takip Bildirimleri */}
      {mesaiNotifications.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-500 text-white animate-pulse">
                  {mesaiNotifications.length} Yeni
                </Badge>
                <span className="font-medium text-blue-800">Mesai Kayıt Bildirimleri</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={markAllMesaiNotificationsAsRead}
                className="text-blue-600 border-blue-300 hover:bg-blue-100"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Tümünü Temizle
              </Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {mesaiNotifications.slice(0, 10).map((notif) => (
                <div 
                  key={notif.id} 
                  className="flex items-center justify-between p-2 bg-white rounded-md border border-blue-100"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">{notif.message}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(notif.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
              {mesaiNotifications.length > 10 && (
                <p className="text-xs text-center text-gray-500">
                  +{mesaiNotifications.length - 10} daha fazla bildirim
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ATANAN GÖREVLER ÖZETİ - Tüm taşeronlar için */}
      {Object.keys(subcontractorAssignments).length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-green-800">
              <ClipboardList className="h-5 w-5" />
              Atanan Taşeron Mesai Takip Görevleri
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(subcontractorAssignments).map(([subId, assignment]) => {
                const subName = getSubcontractorName(subId);
                return (
                  <div 
                    key={subId}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200"
                  >
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-800">{subName || `Taşeron (${subId.slice(0, 8)}...)`}</p>
                        <p className="text-sm text-gray-600">
                          Görevli: <span className="font-semibold text-green-700">{assignment.employee_name}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={() => openTaskModalForSubcontractor(subId)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => handleRemoveAssignment(subId)}
                      >
                        <X className="h-4 w-4 mr-1" />
                        İptal
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company/Subcontractor Tabs */}
      <Tabs value={selectedCompany} onValueChange={setSelectedCompany}>
        <TabsList className="flex flex-wrap gap-2 h-auto p-2 bg-[hsl(var(--muted))]">
          {companyTabs.map(company => {
            const Icon = company.icon;
            const hasAssignment = company.id !== 'fortis' && subcontractorAssignments[company.id];
            return (
              <TabsTrigger 
                key={company.id} 
                value={company.id}
                className="flex items-center gap-2 data-[state=active]:bg-[hsl(var(--brand-700))] data-[state=active]:text-white relative"
                data-testid={`company-tab-${company.id}`}
              >
                <Icon className="h-4 w-4" />
                {company.name}
                {hasAssignment && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {companyTabs.map(company => (
          <TabsContent key={company.id} value={company.id} className="mt-4">
            {/* Taşeron için Görev Atama Kartı (FORTİS hariç) */}
            {company.id !== 'fortis' && (
              <Card className="mb-4 border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="h-5 w-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Mesai Takip Görevi</p>
                        {subcontractorAssignments[company.id] ? (
                          <p className="text-sm text-amber-700">
                            Görevli: <strong>{subcontractorAssignments[company.id].employee_name}</strong>
                          </p>
                        ) : (
                          <p className="text-sm text-amber-600">Henüz görevli atanmadı</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {subcontractorAssignments[company.id] && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleRemoveAssignment(company.id)}
                          data-testid={`remove-task-btn-${company.id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Kaldır
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => openTaskModalForSubcontractor(company.id)}
                        data-testid={`assign-task-btn-${company.id}`}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        {subcontractorAssignments[company.id] ? 'Değiştir' : 'Görev Ata'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Inner Tabs: GÜNLÜK MESAİ, AYLIK MESAİ, FAZLA MESAİLER, İZİNLER */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="tab-daily">
                  <Clock className="h-4 w-4" />
                  GÜNLÜK MESAİ
                </TabsTrigger>
                <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="tab-monthly">
                  <CalendarIcon className="h-4 w-4" />
                  AYLIK MESAİ
                </TabsTrigger>
                <TabsTrigger value="overtime" className="flex items-center gap-2" data-testid="tab-overtime">
                  <Timer className="h-4 w-4" />
                  FAZLA MESAİLER
                </TabsTrigger>
                <TabsTrigger value="leaves" className="flex items-center gap-2" data-testid="tab-leaves">
                  <CalendarIcon className="h-4 w-4" />
                  İZİNLER
                </TabsTrigger>
              </TabsList>

              {/* GÜNLÜK MESAİ Tab */}
              <TabsContent value="daily">
                {/* Date Navigation */}
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="sm" onClick={() => navigateDate(-1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-5 w-5 text-amber-600" />
                          <span className="font-medium text-lg">{formatDisplayDate(currentDate)}</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={goToToday}>
                          Bugün
                        </Button>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigateDate(1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <Card className="bg-gray-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-gray-700">{getSortedEmployees().length}</p>
                      <p className="text-sm text-gray-600">Toplam Çalışan</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {dailyAttendance.filter(a => getSortedEmployees().some(e => e.id === a.employee_id) && a.morning_time).length}
                      </p>
                      <p className="text-sm text-green-600">Sabah Bildirimi</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">
                        {dailyAttendance.filter(a => getSortedEmployees().some(e => e.id === a.employee_id) && a.afternoon_time).length}
                      </p>
                      <p className="text-sm text-blue-600">Öğle Bildirimi</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-amber-700">
                        {getSortedEmployees().length - dailyAttendance.filter(a => getSortedEmployees().some(e => e.id === a.employee_id) && (a.morning_time || a.afternoon_time)).length}
                      </p>
                      <p className="text-sm text-amber-600">Bildirim Yok</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Attendance Table */}
                {getSortedEmployees().length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <AlertCircle className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                      <h3 className="text-xl font-bold mb-2">Çalışan Bulunmuyor</h3>
                      <p className="text-gray-500">
                        {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.name} 
                        kadrosunda bu şubede kayıtlı çalışan yok
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-amber-600" />
                        Günlük Mesai Tablosu - {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead 
                              className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('name')}
                              data-testid="sort-name"
                            >
                              <div className="flex items-center">
                                AD SOYAD
                                {getSortIcon('name')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('position')}
                              data-testid="sort-position"
                            >
                              <div className="flex items-center">
                                GÖREV
                                {getSortIcon('position')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('profession')}
                              data-testid="sort-profession"
                            >
                              <div className="flex items-center">
                                MESLEK
                                {getSortIcon('profession')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold text-center cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('morning')}
                              data-testid="sort-morning"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Sun className="h-4 w-4 text-amber-500" />
                                SABAH MESAİ
                                {getSortIcon('morning')}
                              </div>
                            </TableHead>
                            <TableHead 
                              className="font-bold text-center cursor-pointer hover:bg-gray-100 select-none"
                              onClick={() => handleDailySort('afternoon')}
                              data-testid="sort-afternoon"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <CloudSun className="h-4 w-4 text-orange-500" />
                                ÖĞLE MESAİ
                                {getSortIcon('afternoon')}
                              </div>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getSortedEmployees().map(emp => {
                            const att = getDailyAttendanceForEmployee(emp.id, emp.name);
                            const morningTime = att?.morning_time || att?.sabah_time;
                            const afternoonTime = att?.afternoon_time || att?.oglen_time;
                            const empNote = attendanceNotes[emp.id]?.note;
                            const cellClickProps = readOnly ? {} : {
                              onClick: () => openNoteDialog(emp),
                              className: 'text-center cursor-pointer hover:bg-amber-50 transition-colors',
                              title: empNote ? `Not: ${empNote}` : 'Not eklemek için tıklayın'
                            };
                            return (
                              <TableRow key={emp.id} data-testid={`attendance-row-${emp.id}`}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    {emp.name}
                                    {empNote && (
                                      <span title={`Not: ${empNote}`} data-testid={`attendance-note-indicator-${emp.id}`}>
                                        <AlertCircle className="h-4 w-4 text-amber-500" />
                                      </span>
                                    )}
                                  </div>
                                  {empNote && (
                                    <p className="text-xs text-amber-700 mt-0.5 italic max-w-[220px] truncate" title={empNote}>
                                      {empNote}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell>{emp.position_name || emp.gorev || '-'}</TableCell>
                                <TableCell>{emp.profession || emp.meslek || '-'}</TableCell>
                                <TableCell {...(readOnly ? { className: 'text-center' } : cellClickProps)} data-testid={`attendance-morning-${emp.id}`}>
                                  {morningTime ? (
                                    <Badge className="bg-green-100 text-green-700 font-mono">
                                      {typeof morningTime === 'string' && morningTime.includes(':') ? morningTime : formatTime(morningTime)}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-400">
                                      Bildirim Yok
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell {...(readOnly ? { className: 'text-center' } : cellClickProps)} data-testid={`attendance-afternoon-${emp.id}`}>
                                  {afternoonTime ? (
                                    <Badge className="bg-blue-100 text-blue-700 font-mono">
                                      {typeof afternoonTime === 'string' && afternoonTime.includes(':') ? afternoonTime : formatTime(afternoonTime)}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-400">
                                      Bildirim Yok
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* AYLIK MESAİ Tab */}
              <TabsContent value="monthly">
                {/* Month Navigation */}
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-lg">
                          {monthlyDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {monthlyLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                  </div>
                ) : monthlyData.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <CalendarIcon className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                      <h3 className="text-xl font-bold mb-2">Veri Bulunmuyor</h3>
                      <p className="text-gray-500">Bu ay için mesai kaydı yok</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5 text-amber-600" />
                        Aylık Mesai Tablosu - {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="w-full">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50">
                              <TableHead className="sticky left-0 bg-gray-50 z-10 min-w-[150px] font-bold">AD SOYAD</TableHead>
                              {[...Array(getDaysInMonth(monthlyDate))].map((_, i) => (
                                <TableHead key={i} className="text-center min-w-[32px] font-bold text-xs">
                                  {i + 1}
                                </TableHead>
                              ))}
                              <TableHead className="text-center min-w-[40px] font-bold bg-green-50">G</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-yellow-50">YG</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-red-50">X</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-blue-50">Üİ</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-orange-50">Üsİ</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-purple-50">R</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-cyan-50">Yİ</TableHead>
                              <TableHead className="text-center min-w-[40px] font-bold bg-gray-100">RT</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {monthlyData.map(emp => {
                              const att = emp.attendance || {};
                              const empStartDate = getEmployeeStartDate(emp);
                              const counts = {
                                tam_gun: 0, yarim_gun: 0, yok: 0, izinli: 0, 
                                ucretsiz_izinli: 0, raporlu: 0, yillik_izin: 0, tatil: 0
                              };
                              // İşe giriş tarihinden önceki günleri sayma
                              for (let d = 1; d <= getDaysInMonth(monthlyDate); d++) {
                                const cellDate = new Date(monthlyDate.getFullYear(), monthlyDate.getMonth(), d);
                                if (empStartDate && cellDate < empStartDate) continue;
                                const status = att[String(d)];
                                if (counts[status] !== undefined) counts[status]++;
                              }
                              
                              return (
                                <TableRow key={emp.id}>
                                  <TableCell className="sticky left-0 bg-white z-10 font-medium">{emp.name}</TableCell>
                                  {[...Array(getDaysInMonth(monthlyDate))].map((_, i) => {
                                    const dayKey = String(i + 1);
                                    const status = att[dayKey];
                                    const checkin = emp.daily_checkins?.[dayKey] || {};
                                    const cellDateStr = `${monthlyDate.getFullYear()}-${String(monthlyDate.getMonth() + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
                                    const dayNote = monthlyNotes[`${emp.id}|${cellDateStr}`]?.note;
                                    const cellDate = new Date(monthlyDate.getFullYear(), monthlyDate.getMonth(), i + 1);
                                    const beforeHire = empStartDate && cellDate < empStartDate;
                                    if (beforeHire) {
                                      // İşe girmeden önceki günler: pasif, tıklanamaz
                                      return (
                                        <TableCell
                                          key={i}
                                          className="text-center p-1 bg-gray-100 text-gray-300"
                                          title="İşe giriş tarihinden önce"
                                          data-testid={`monthly-cell-disabled-${emp.id}-${i + 1}`}
                                        >
                                          <span className="text-xs">·</span>
                                        </TableCell>
                                      );
                                    }
                                    return (
                                      <TableCell
                                        key={i}
                                        className={`text-center p-1 ${dayNote ? 'bg-red-100 ring-1 ring-red-300' : ''} ${readOnly ? '' : 'cursor-pointer hover:bg-red-50'}`}
                                        title={dayNote ? `Not: ${dayNote}` : (readOnly ? '' : 'Not eklemek için tıklayın')}
                                        onClick={readOnly ? undefined : () => openNoteDialog(emp, cellDateStr)}
                                        data-testid={`monthly-cell-${emp.id}-${i + 1}`}
                                      >
                                        <div className="flex flex-col items-center">
                                          {getStatusBadge(status)}
                                          {/* Sabah/Akşam Check-in Göstergesi (daily_attendance'dan) */}
                                          <div className="flex gap-0.5 mt-0.5">
                                            <span className={`text-[9px] font-extrabold ${checkin.sabah ? 'text-green-600' : 'text-red-600'}`}>
                                              {checkin.sabah ? '+' : '-'}
                                            </span>
                                            <span className={`text-[9px] font-extrabold ${checkin.oglen ? 'text-green-600' : 'text-red-600'}`}>
                                              {checkin.oglen ? '+' : '-'}
                                            </span>
                                          </div>
                                          {dayNote && (
                                            <AlertCircle className="h-3 w-3 text-red-600 mt-0.5" />
                                          )}
                                        </div>
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell className="text-center font-bold bg-green-50">{counts.tam_gun}</TableCell>
                                  <TableCell className="text-center font-bold bg-yellow-50">{counts.yarim_gun}</TableCell>
                                  <TableCell className="text-center font-bold bg-red-50">{counts.yok}</TableCell>
                                  <TableCell className="text-center font-bold bg-blue-50">{counts.izinli}</TableCell>
                                  <TableCell className="text-center font-bold bg-orange-50">{counts.ucretsiz_izinli}</TableCell>
                                  <TableCell className="text-center font-bold bg-purple-50">{counts.raporlu}</TableCell>
                                  <TableCell className="text-center font-bold bg-cyan-50">{counts.yillik_izin}</TableCell>
                                  <TableCell className="text-center font-bold bg-gray-100">{counts.tatil}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* Legend */}
                <Card className="mt-4">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1">{getStatusBadge('tam_gun')} Tam Gün (G)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('yarim_gun')} Yarım Gün (YG)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('yok')} Gelmedi (X)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('izinli')} Ücretli İzinli (Üİ)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('ucretsiz_izinli')} Ücretsiz İzinli (Üsİ)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('raporlu')} Raporlu (R)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('yillik_izin')} Yıllık İzin (Yİ)</span>
                      <span className="flex items-center gap-1">{getStatusBadge('tatil')} Resmi Tatil (RT)</span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* FAZLA MESAİLER Tab */}
              <TabsContent value="overtime">
                {/* Year/Month Selection */}
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-center gap-4">
                      <Select value={String(overtimeYear)} onValueChange={(v) => setOvertimeYear(parseInt(v))}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026].map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={String(overtimeMonth)} onValueChange={(v) => setOvertimeMonth(parseInt(v))}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'].map((m, i) => (
                            <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {overtimeLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                  </div>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Timer className="h-5 w-5 text-amber-600" />
                        Fazla Mesai Tablosu - {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-bold">AD SOYAD</TableHead>
                            <TableHead className="font-bold">GÖREV</TableHead>
                            <TableHead className="font-bold">MESLEK</TableHead>
                            <TableHead className="font-bold text-center">HAFTA İÇİ (Saat)</TableHead>
                            <TableHead className="font-bold text-center">HAFTA SONU (Saat)</TableHead>
                            <TableHead className="font-bold text-center">TATİL (Saat)</TableHead>
                            <TableHead className="font-bold text-center">TOPLAM (Saat)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {employees.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                                Bu şirket için çalışan bulunmuyor
                              </TableCell>
                            </TableRow>
                          ) : (
                            employees.map(emp => {
                              let monthRecords = [];
                              const overtimeData = emp.overtime_records;
                              if (overtimeData && typeof overtimeData === 'object') {
                                const yearData = overtimeData[String(overtimeYear)];
                                if (yearData && typeof yearData === 'object') {
                                  const monthData = yearData[String(overtimeMonth)];
                                  if (Array.isArray(monthData)) {
                                    monthRecords = monthData;
                                  }
                                }
                              } else if (Array.isArray(overtimeData)) {
                                monthRecords = overtimeData.filter(r => {
                                  if (!r || !r.date) return false;
                                  const d = new Date(r.date);
                                  return d.getFullYear() === overtimeYear && d.getMonth() + 1 === overtimeMonth;
                                });
                              }
                              
                              const weekday = monthRecords.filter(r => r.type === 'weekday').reduce((sum, r) => sum + (r.hours || 0), 0);
                              const weekend = monthRecords.filter(r => r.type === 'weekend').reduce((sum, r) => sum + (r.hours || 0), 0);
                              const holiday = monthRecords.filter(r => r.type === 'holiday').reduce((sum, r) => sum + (r.hours || 0), 0);
                              const total = weekday + weekend + holiday;
                              
                              return (
                                <TableRow key={emp.id}>
                                  <TableCell className="font-medium">{emp.name}</TableCell>
                                  <TableCell>{emp.position_name || emp.gorev || '-'}</TableCell>
                                  <TableCell>{emp.profession || emp.meslek || '-'}</TableCell>
                                  <TableCell className="text-center">{weekday || '-'}</TableCell>
                                  <TableCell className="text-center">{weekend || '-'}</TableCell>
                                  <TableCell className="text-center">{holiday || '-'}</TableCell>
                                  <TableCell className="text-center font-bold">{total || '-'}</TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* İZİNLER Tab */}
              <TabsContent value="leaves">
                <div className="space-y-4">
                  {/* Yıl Seçimi */}
                  <div className="flex items-center gap-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5 text-cyan-600" />
                      <span className="font-medium text-cyan-800">Yıl:</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLeavesYear(leavesYear - 1)}
                      >
                        ← Önceki Yıl
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLeavesYear(new Date().getFullYear())}
                      >
                        Bu Yıl
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLeavesYear(leavesYear + 1)}
                      >
                        Sonraki Yıl →
                      </Button>
                    </div>
                    <div className="ml-auto">
                      <span className="text-lg font-bold text-cyan-700">{leavesYear}</span>
                    </div>
                  </div>

                  {/* Yıllık Özet Tablosu */}
                  <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-auto max-h-[600px]">
                    <Table>
                      <TableHeader className="sticky top-0 z-30">
                        <TableRow className="bg-cyan-100">
                          <TableHead 
                            className="sticky left-0 bg-cyan-100 z-10 font-bold cursor-pointer hover:bg-cyan-200 select-none"
                            onClick={() => handleLeavesSort('name')}
                          >
                            Çalışan
                            {leavesSortColumn === 'name' && (
                              leavesSortDirection === 'asc' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                            )}
                          </TableHead>
                          {monthNames.map((month, idx) => (
                            <TableHead key={idx} className="text-center font-bold min-w-[100px]">
                              {month}
                            </TableHead>
                          ))}
                          <TableHead 
                            className="text-center font-bold bg-red-100 min-w-[70px] cursor-pointer hover:bg-red-200 select-none"
                            onClick={() => handleLeavesSort('absent')}
                          >
                            Gelmedi
                            {leavesSortColumn === 'absent' && (
                              leavesSortDirection === 'asc' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                            )}
                          </TableHead>
                          <TableHead 
                            className="text-center font-bold bg-blue-100 min-w-[90px] cursor-pointer hover:bg-blue-200 select-none"
                            onClick={() => handleLeavesSort('mazeret_izni')}
                          >
                            Ücretli İzin
                            {leavesSortColumn === 'mazeret_izni' && (
                              leavesSortDirection === 'asc' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                            )}
                          </TableHead>
                          <TableHead 
                            className="text-center font-bold bg-cyan-100 min-w-[100px] cursor-pointer hover:bg-cyan-200 select-none"
                            onClick={() => handleLeavesSort('ucretsiz_izin')}
                          >
                            Ücretsiz İzin
                            {leavesSortColumn === 'ucretsiz_izin' && (
                              leavesSortDirection === 'asc' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                            )}
                          </TableHead>
                          <TableHead 
                            className="text-center font-bold bg-purple-100 min-w-[70px] cursor-pointer hover:bg-purple-200 select-none"
                            onClick={() => handleLeavesSort('raporlu')}
                          >
                            Raporlu
                            {leavesSortColumn === 'raporlu' && (
                              leavesSortDirection === 'asc' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                            )}
                          </TableHead>
                          <TableHead 
                            className="text-center font-bold bg-yellow-100 min-w-[80px] cursor-pointer hover:bg-yellow-200 select-none"
                            onClick={() => handleLeavesSort('yillik_izin')}
                          >
                            Yıllık İzin
                            {leavesSortColumn === 'yillik_izin' && (
                              leavesSortDirection === 'asc' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />
                            )}
                          </TableHead>
                          <TableHead className="text-center font-bold bg-green-100 min-w-[120px]">Kalan Y. İzin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leavesLoading ? (
                          <TableRow>
                            <TableCell colSpan={20} className="text-center py-8">
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
                                Yükleniyor...
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : sortedLeavesData.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={20} className="text-center py-8 text-gray-500">
                              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                              <p>Aktif çalışan bulunamadı</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedLeavesData.map((emp) => (
                            <TableRow key={emp.employee_id} className="hover:bg-cyan-50">
                              <TableCell className="sticky left-0 bg-white z-10 font-medium border-r">
                                <div className="flex items-center gap-2">
                                  {emp.name}
                                  {emp.termination_date && (
                                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                                      Ayrıldı
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                                const monthData = emp.months?.[month] || {};
                                const hasData = Object.values(monthData).some(v => v > 0);
                                return (
                                  <TableCell key={month} className="text-center p-2">
                                    {hasData ? (
                                      <div className="text-xs space-y-0.5">
                                        {monthData.annual > 0 && (
                                          <div className="text-yellow-600">Y.İzin: {monthData.annual}</div>
                                        )}
                                        {monthData.sick > 0 && (
                                          <div className="text-purple-600">Rapor: {monthData.sick}</div>
                                        )}
                                        {monthData.leave > 0 && (
                                          <div className="text-blue-600">İzin: {monthData.leave}</div>
                                        )}
                                        {monthData.unpaid_leave > 0 && (
                                          <div className="text-cyan-600">Üsİz: {monthData.unpaid_leave}</div>
                                        )}
                                        {monthData.absent > 0 && (
                                          <div className="text-red-600">Gelmedi: {monthData.absent}</div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-300">-</span>
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-center font-bold bg-red-50 text-red-700">{emp.totals?.absent || '-'}</TableCell>
                              <TableCell className="text-center font-bold bg-blue-50 text-blue-700">{emp.totals?.paid_leave || emp.totals?.leave || '-'}</TableCell>
                              <TableCell className="text-center font-bold bg-cyan-50 text-cyan-700">{emp.totals?.unpaid_leave || '-'}</TableCell>
                              <TableCell className="text-center font-bold bg-purple-50 text-purple-700">{emp.totals?.sick || '-'}</TableCell>
                              <TableCell className="text-center font-bold bg-yellow-50 text-yellow-700">{emp.totals?.annual || '-'}</TableCell>
                              <TableCell className={`text-center font-bold ${emp.remaining_annual_leave < 0 ? 'bg-red-100 text-red-700' : 'bg-green-50 text-green-700'}`}>
                                {emp.remaining_annual_leave ?? '-'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
        ))}
      </Tabs>

      {/* Taşeron Bazlı Görev Atama Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              Mesai Takip Görevi Ata
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Taşeron Bilgisi */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                Seçilen görevli, işaretlenen <strong>taşeron(lar)ın</strong> mesai girişlerini İş Paneli'nde kaydedebilecek.
                Birden fazla taşeron seçerek tek seferde atayabilirsiniz.
              </p>
            </div>

            {/* Çoklu Taşeron Seçimi */}
            <div className="space-y-2">
              <Label>Görevin Uygulanacağı Taşeron(lar)</Label>
              <div className="max-h-44 overflow-y-auto border rounded-lg divide-y" data-testid="task-subcontractor-list">
                {companyTabs.filter(c => c.id !== 'fortis').map(company => {
                  const checked = selectedSubcontractorsForTask.includes(company.id);
                  const assignedTo = subcontractorAssignments[company.id]?.employee_name;
                  return (
                    <label
                      key={company.id}
                      className="flex items-center gap-3 p-2.5 hover:bg-amber-50 cursor-pointer"
                      data-testid={`task-sub-option-${company.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleSubcontractorForTask(company.id)}
                        data-testid={`task-sub-check-${company.id}`}
                      />
                      <span className="text-sm flex-1">
                        {company.name}
                        {assignedTo && (
                          <span className="text-xs text-gray-400 ml-1">(mevcut: {assignedTo})</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
              {selectedSubcontractorsForTask.length > 0 && (
                <p className="text-xs text-amber-600">{selectedSubcontractorsForTask.length} taşeron seçildi</p>
              )}
            </div>
            
            {/* Çalışan Seçimi */}
            {loadingAssignments ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Görevlendirilecek Çalışan (FORTİS Kadrolu)</Label>
                <Select value={selectedEmployeeForTask} onValueChange={setSelectedEmployeeForTask}>
                  <SelectTrigger data-testid="employee-select">
                    <SelectValue placeholder="Çalışan seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allBranchEmployees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} {emp.position_name ? `(${emp.position_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleAssignTask}
              disabled={taskSubmitting || loadingAssignments || !selectedEmployeeForTask || selectedSubcontractorsForTask.length === 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-assignment-btn"
            >
              {taskSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                'Görev Ata'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mesai Notu Modalı */}
      <Dialog open={noteDialogOpen} onOpenChange={(o) => { if (!o) { setNoteDialogOpen(false); setNoteEmployee(null); setNoteText(''); } }}>
        <DialogContent className="max-w-md" data-testid="attendance-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-amber-600" /> Mesai Notu
            </DialogTitle>
            <DialogDescription>
              {noteEmployee?.name} · {noteDate || formatDateStr(currentDate)} — bu güne ait mesai takibi için not ekleyin (örn. "işaretlendi ama işe gelmedi").
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="attendance-note-text">Not</Label>
            <Textarea
              id="attendance-note-text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              placeholder="Örn: Sistemde işaretli ama işe gelmedi / saat hatalı girilmiş..."
              data-testid="attendance-note-textarea"
            />
            {noteEmployee && attendanceNotes[noteEmployee.id]?.updated_by_name && (
              <p className="text-xs text-gray-400">
                Son güncelleyen: {attendanceNotes[noteEmployee.id].updated_by_name}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNoteDialogOpen(false); setNoteEmployee(null); setNoteText(''); }}>İptal</Button>
            <Button onClick={saveNote} disabled={noteSaving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="attendance-note-save-btn">
              {noteSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Kaydediliyor...</> : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchAttendance;
